/**
 * cosmicJourneys.js
 * -----------------
 * Vendored, presentation-only copy of the Career Explorer + Pivot journey
 * content, rendered in-app in the cosmic Careers tab (replacing the old
 * link-out to bookbran.github.io/APC-Journeys).
 *
 * SOURCE OF TRUTH: bookbran/apc-journeys-data (src/index.ts), the canonical
 * npm package consumed by the Inquiry Call Guide, Intake Builder, and (planned)
 * CRM milestone library. This file is a faithful copy of the `explorer` and
 * `pivot` slices, kept here as a local vendor so the CRA build + Firebase deploy
 * do not depend on a private git dependency. It is a marketing/results display,
 * NOT a billing/milestone consumer, so it is intentionally outside the package.
 *
 * SYNC RULE: if a phase, session, activity, or homework line changes in
 * apc-journeys-data, mirror the change here. Phases/sessions/activities/homework
 * below are verbatim from the package; only the cosmic-render fields
 * (`accent`, `destination`, `parallelSupport`) are added for the dark theme.
 * The print `color` per phase (navy/blue/orange) is replaced by `accent` because
 * brand navy is invisible on the cosmic navy background.
 *
 * Last synced: 2026-06-27 (apc-journeys-data main).
 */

// Cosmic phase accents — a purple -> blue -> orange ramp that reads on dark
// navy and warms toward the destination. (The source print colors do not.)
const ACCENT = {
  purple: '#8b8bc4',
  blue: '#2DA3CB',
  orange: '#f15a24',
};

export const COSMIC_JOURNEYS = [
  {
    key: 'explorer',
    name: 'Career Explorer',
    icon: 'ui_telescope',
    tagline: 'Who you are, what energizes you, what could be next',
    description:
      "For when you don't yet know what's next and you have the runway to do the work of figuring it out. Three phases over about seven sessions: self-discovery, guided mapping with your coach, and a pathway plan you walk away with.",
    sessionCount: '~7 sessions · 3 phases',
    bestFor: [
      'You have months of runway',
      'You want insight before action',
      'Curious and ready to explore',
    ],
    recommendedCoaches: 'Krute, Autumn, Ursina',
    phases: [
      {
        id: 1,
        name: 'Self-Discovery',
        subtitle: 'Who you are, what energizes you',
        accent: ACCENT.purple,
        sessions: [
          {
            id: 1,
            title: 'Story & Interests',
            activities: [
              'Share life story & career history',
              'Explore interests across decades',
              'Identify energizing themes',
              'Build psychological safety',
            ],
            homework: 'IPIP 300 & 16 Personalities assessments',
          },
          {
            id: 2,
            title: 'Personality & Self-Discovery',
            activities: [
              'Debrief assessment results',
              'Map traits to past work',
              'Identify thriving environments',
              'Connect insights to directions',
            ],
            homework: 'Motivated Skills Matrix & Gallup Strengths',
          },
          {
            id: 3,
            title: 'Strengths & Preferences',
            activities: [
              'Review strengths assessment',
              'Build Career Preferences Model',
              'Connect strengths to values',
              'Discuss with trusted peer',
            ],
            homework: 'Refine preferences; discuss top criteria',
          },
        ],
      },
      {
        id: 2,
        name: 'Guided Mapping',
        subtitle: 'Exploring the world of possibilities together',
        accent: ACCENT.blue,
        sessions: [
          {
            id: 4,
            title: 'Industry Discovery',
            activities: [
              'Finalize Preferences Model',
              'AI & O*NET exploration',
              'Real-time energy check',
              'Select 3–4 target industries',
            ],
            homework: 'Read articles on two industries of interest',
          },
          {
            id: 5,
            title: 'Org & Role Exploration',
            activities: [
              'Co-browse target companies',
              'Analyze job postings together',
              'Build target list in real-time',
              'Note curiosity triggers',
            ],
            homework: 'Curiosity conversation outreach',
          },
          {
            id: 6,
            title: 'Networking for Exploration',
            activities: [
              'Debrief curiosity conversation',
              'Draft outreach messages',
              'Role-play interview questions',
              'Identify confirmation needs',
            ],
            homework: 'Send outreach; conduct interviews',
          },
        ],
      },
      {
        id: 3,
        name: 'Pathway Planning',
        subtitle: 'Your plan forward',
        accent: ACCENT.orange,
        sessions: [
          {
            id: 7,
            title: 'Synthesis & Roadmap',
            activities: [
              'Review all learnings',
              'Filter via Preferences Model',
              'Co-create transition roadmap',
              'Define clear next steps',
            ],
            homework: null,
          },
        ],
      },
    ],
    destination: {
      title: 'Your Next Chapter',
      note: 'Clarity, direction, and a concrete roadmap for your career transition.',
    },
  },
  {
    key: 'pivot',
    name: 'Career Pivot',
    icon: 'ui_rocket',
    tagline: 'Something different, and bridges to get there within a year',
    description:
      'For when you want change and need a role within months, not years. Three phases over about eight sessions: self-discovery, guided mapping with your coach, and search & interview prep, with a materials specialist working your resume and LinkedIn in parallel.',
    sessionCount: '~8 sessions · 3 phases',
    bestFor: [
      'You want different work soon',
      'A role within about 12 months',
      'Ready to act while exploring',
    ],
    recommendedCoaches: 'Aimee, Victoria, Emily, Autumn',
    phases: [
      {
        id: 1,
        name: 'Self-Discovery',
        subtitle: 'Sessions 1–2',
        accent: ACCENT.purple,
        sessions: [
          {
            id: 1,
            title: 'Review & Insight',
            activities: [
              'Walk through resume & history',
              'Identify energy drains & gains',
              'Map patterns & core strengths',
              'Foundation for preferences model',
            ],
            homework: 'Complete Motivated Skills Matrix & Gallup Strengths',
          },
          {
            id: 2,
            title: 'Preferences Model',
            activities: [
              'Debrief assessments',
              'Build Career Preferences Model (6–8 criteria)',
              'Connect strengths to directions',
              'Discuss model with a confidant',
            ],
            homework: 'Refine model; coach preps initial research',
          },
        ],
      },
      {
        id: 2,
        name: 'Guided Mapping',
        subtitle: 'Sessions 3–5 · Coach & client side-by-side',
        accent: ACCENT.blue,
        sessions: [
          {
            id: 3,
            title: 'Industry Discovery',
            activities: [
              'Finalize preferences model',
              'Co-explore industries via AI & O*NET',
              'Review coach-researched listings',
              'Real-time energy check',
            ],
            homework: 'Browse LinkedIn of 2–3 interesting companies',
          },
          {
            id: 4,
            title: 'Targeting & LinkedIn',
            activities: [
              'Analyze target companies & profiles',
              'Finalize target organization list',
              "Coach-led LinkedIn 'About' draft",
              'Update Experience section',
            ],
            homework: 'Post LinkedIn About; meet Joanie for docs',
          },
          {
            id: 5,
            title: 'Networking Launch',
            activities: [
              "Review Joanie's materials",
              'Draft outreach messages together',
              'Build networking target list',
              'Set application rhythm',
            ],
            homework: 'Send drafted messages; apply to 3–5 roles',
          },
        ],
      },
      {
        id: 3,
        name: 'Search & Interview',
        subtitle: 'Sessions 6–8 · Show up with confidence',
        accent: ACCENT.orange,
        sessions: [
          {
            id: 6,
            title: 'Strategy & Story',
            activities: [
              'Check networking progress',
              "Build 'Tell Me About Yourself'",
              'Intro C-C-A-R framework',
              'Practice intro out loud',
            ],
            homework: 'Practice intro; draft two C-C-A-R stories',
          },
          {
            id: 7,
            title: 'Refinement',
            activities: [
              'Strengthen C-C-A-R stories',
              'Practice behavioral questions',
              'Address gaps or pivots',
              'Develop questions for interviewers',
            ],
            homework: 'Revise stories; continue applications',
          },
          {
            id: 8,
            title: 'Mock Interview',
            activities: [
              'Full realistic mock interview',
              'Structured debrief & feedback',
              'Final confidence prep',
              'Launch into your new chapter',
            ],
            homework: null,
          },
        ],
      },
    ],
    // Pivot's "Materials Specialist / Meet Joanie" side note, integrated in Phase 2.
    parallelSupport: {
      title: 'A materials specialist, in parallel',
      note: 'Mid-journey you partner with Joanie to turn your insights into strong documents, woven in during the mapping phase.',
      items: ['Resume tailoring', 'Cover letter strategy', 'LinkedIn optimization'],
      afterPhaseId: 2,
    },
    destination: {
      title: 'Launch',
      note: 'Materials ready, story sharp, and the confidence to step into your new chapter.',
    },
  },
];
